import React, { useState, useEffect } from "react";
import { Link, NavLink } from "react-router-dom";
import { useTheme } from "../../contexts/ThemeContext";
import {
  FiImage,
} from "react-icons/fi";
import { FaBookOpen, FaFileExcel, FaFilePdf } from "react-icons/fa";
import { FaArrowRightArrowLeft } from "react-icons/fa6";
const Header = () => {
  const { theme, toggleTheme } = useTheme();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Handle scroll effect for header
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  // Navigation links data
  const navLinks = [
    {
      name: "Image Tools",
      to: "/image-resizer",
      icon: <FiImage className="w-5 h-5 text-blue-500" />,
      activeIconClass: "text-blue-600 dark:text-blue-400",
      inactiveIconClass: "text-blue-500 dark:text-blue-300",
    },
    {
      name: "PDF Tools",
      to: "/pdf-resizer",
      icon: <FaFilePdf className="w-5 h-5 text-red-500" />,
      activeIconClass: "text-red-600 dark:text-red-400",
      inactiveIconClass: "text-red-500 dark:text-red-300",
    },
    {
      name: "All Converts",
      to: "/converters",
      icon: <FaArrowRightArrowLeft className="w-5 h-5 text-green-500" />,
      activeIconClass: "text-green-600 dark:text-green-400",
      inactiveIconClass: "text-green-500 dark:text-green-300",
    },
    {
      name: "Office Tools",
      to: "/office-tools",
      icon: <FaFileExcel className="w-5 h-5 text-green-500" />,
      activeIconClass: "text-purple-600 dark:text-purple-400",
      inactiveIconClass: "text-purple-500 dark:text-purple-300",
    },
    {
      name: "Tool Guides",
      to: "/tool-guides",
      icon: <FaBookOpen className="w-5 h-5 text-blue-500" />,
      activeIconClass: "text-yellow-600 dark:text-yellow-400",
      inactiveIconClass: "text-yellow-500 dark:text-yellow-300",
    },
  ];

  return (
    <header
      className={`sticky top-0 z-50 transition-all duration-300 ${scrolled
        ? "bg-white/90 dark:bg-gray-900/90 backdrop-blur-md shadow-sm"
        : "bg-white dark:bg-gray-900"
        }`}
    >
      <div className="container mx-auto px-4 py-3">
        <div className="flex justify-between items-center">
          {/* Logo/Brand */}
          <div className="flex items-center">
            <Link
              to="/"
              className="flex items-center group"
              onClick={closeMobileMenu}
            >
              <div className="relative w-[200px]">
                <img
                  src="/logo.png"
                  alt="QuikResize Logo"
                  className="h-6 w-auto mr-2"
                />
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex ml-10 space-x-1">
              {navLinks.map((link) => (
                <div key={link.to} className="relative">
                  <NavLink
                    to={link.to}
                    className={({ isActive }) =>
                      `px-4 py-2 rounded-lg text-md font-medium transition-all duration-200 flex items-center space-x-2 ${isActive
                        ? "bg-blue-50 dark:bg-gray-800"
                        : "hover:bg-gray-100 dark:hover:bg-gray-800"
                      }`
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <span
                          className={`flex-shrink-0 ${isActive
                            ? link.activeIconClass
                            : link.inactiveIconClass
                            }`}
                        >
                          {link.icon}
                        </span>
                        <span
                          className={
                            isActive
                              ? "text-blue-600 dark:text-blue-400"
                              : "text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                          }
                        >
                          {link.name}
                        </span>
                      </>
                    )}
                  </NavLink>
                </div>
              ))}
            </nav>
          </div>

          {/* Right side controls */}
          <div className="flex items-center space-x-3">
            {/* Theme toggle */}
            <button
              onClick={toggleTheme}
              className="p-2 rounded-full text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200"
              aria-label={`Switch to ${theme === "dark" ? "light" : "dark"} mode`}
            >
              {theme === "dark" ? (
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
                  />
                </svg>
              ) : (
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
                  />
                </svg>
              )}
            </button>

            {/* CTA Button - Desktop */}
            <div className="hidden md:block">
              <Link
                to="/image-resizer"
                className="px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg text-sm font-medium hover:from-blue-700 hover:to-purple-700 transition-all duration-300 shadow-md hover:shadow-lg flex items-center"
                onClick={closeMobileMenu}
              >
                <svg
                  className="w-4 h-4 mr-2 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                  />
                </svg>
                Get Started
              </Link>
            </div>

            {/* Mobile menu button */}
            <button
              className="md:hidden p-2 rounded-lg text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
              aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? (
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              ) : (
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        <div
          className={`md:hidden transition-all duration-300 ease-in-out overflow-hidden ${mobileMenuOpen ? "max-h-screen mt-3" : "max-h-0"
            }`}
        >
          <nav className="space-y-2 pb-2">
            {navLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                className={({ isActive }) =>
                  `block px-4 py-3 rounded-lg text-base font-medium transition-colors duration-200 flex items-center space-x-3 ${isActive
                    ? "bg-blue-50 dark:bg-gray-800"
                    : "hover:bg-gray-100 dark:hover:bg-gray-800"
                  }`
                }
                onClick={closeMobileMenu}
              >
                {({ isActive }) => (
                  <>
                    <span
                      className={`flex-shrink-0 ${isActive
                        ? link.activeIconClass
                        : link.inactiveIconClass
                        }`}
                    >
                      {link.icon}
                    </span>
                    <span
                      className={
                        isActive
                          ? "text-blue-600 dark:text-blue-400"
                          : "text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                      }
                    >
                      {link.name}
                    </span>
                  </>
                )}
              </NavLink>
            ))}
            <div className="pt-2">
              <Link
                to="/image-resizer"
                className="block w-full px-4 py-3 rounded-lg text-base font-medium text-center bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700 transition-all duration-300 shadow-md flex items-center justify-center space-x-2"
                onClick={closeMobileMenu}
              >
                <svg
                  className="w-4 h-4 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                  />
                </svg>
                <span>Get Started</span>
              </Link>
            </div>
          </nav>
        </div>
      </div>
    </header>
  );
};

export default Header;